import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { Employee } from './../../model/employee.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AreaData } from 'src/app/model/area.model';
import { Role } from 'src/app/model/role';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss'],
})
export class EmployeeDialog implements OnInit {

  isAdmin:boolean = false;
  isSysAdmin:boolean = false;
  searchValue: string = '';
  photoPreview: SafeUrl | null = null;
  documentObjectUrl: string | null = null;
  documentPreview: SafeUrl | null = null;
  documentPreviewIsImage: boolean = false;
  allEmployees: Employee[] = [];
  
  constructor(
    public dialogRef: MatDialogRef<EmployeeDialog>,
    @Inject(MAT_DIALOG_DATA) public data: Employee,
    private apiService: ApiService,
    private _snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {
    const employeeId = window.localStorage.getItem("loggedInUser");

    this.apiService.getEmplyeeDetailsById(employeeId).subscribe((response) => {
         
         let roles = response.roles.map((role : Role)=>role.roleName);
         if(roles.includes("admin")){
          this.isAdmin = true;     
         }   
         if(roles.includes("sysadmin")){
          this.isSysAdmin = true;     
         }   
         this.data.employeeRole = roles.join(",");
         
      });

    // Load all employees for search functionality
    this.loadAllEmployees();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
  
  areas: AreaData[];
  employeeRole: string;
  ngOnInit() {
    this.apiService.getAreaDetails().subscribe((data) => {
        this.areas = data.areas;
    });
  }

  loadAllEmployees() {
    this.apiService.getEmployees().subscribe((data) => {
      this.allEmployees = data.employees || [];
    });
  }

  searchEmployee() {
    if (!this.searchValue || this.searchValue.trim() === '') {
      return;
    }

    const searchLower = this.searchValue.toLowerCase().trim();
    const foundEmployee = this.allEmployees.find(emp => 
      emp.employeeId.toString() === searchLower || 
      emp.employeeName.toLowerCase().includes(searchLower)
    );

    if (foundEmployee) {
      // Load the found employee's complete details
      this.apiService.getEmployeeDetailsById(foundEmployee.employeeId.toString()).subscribe((employeeData) => {
        Object.assign(this.data, employeeData);
        this.data.employeeAction = 'update';
        this._snackBar.open('Employee found and loaded for editing', 'Close', {
          duration: 3000,
        });
      });
    } else {
      this._snackBar.open('Employee not found', 'Close', {
        duration: 3000,
      });
    }
  }

  onPhotoSelect(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const dataUrl = e.target.result as string;
        // Store the base64 content only for the backend byte[] (LONGBLOB).
        // The template prepends "data:image/jpeg;base64," when displaying.
        const base64Content = dataUrl.split(',')[1];
        this.data.employeePhoto = base64Content;
        this.photoPreview = this.sanitizer.bypassSecurityTrustUrl(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  }

  onDocumentSelect(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.data.documentName = file.name;
      this.data.documentType = file.type;
      this.data.documentSize = file.size;

      // Build a preview/full-view URL for the freshly selected file.
      if (this.documentObjectUrl) {
        URL.revokeObjectURL(this.documentObjectUrl);
      }
      this.documentObjectUrl = URL.createObjectURL(file);
      this.documentPreviewIsImage = (file.type || '').startsWith('image/');
      this.documentPreview = this.documentPreviewIsImage
        ? this.sanitizer.bypassSecurityTrustUrl(this.documentObjectUrl)
        : null;

      // Read the file content into documentPath as a base64 data URL so it is
      // persisted to the DB (document_path is LONGTEXT).
      const reader = new FileReader();
      reader.onload = () => {
        this.data.documentPath = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removePhoto() {
    this.data.employeePhoto = null;
    this.photoPreview = null;
  }

  removeDocument() {
    if (this.documentObjectUrl) {
      URL.revokeObjectURL(this.documentObjectUrl);
    }
    this.documentObjectUrl = null;
    this.documentPreview = null;
    this.documentPreviewIsImage = false;
    this.data.employeeDocument = null;
    this.data.documentPath = null;
    this.data.documentName = null;
    this.data.documentType = null;
    this.data.documentSize = null;
  }

  viewDocument() {
    // Prefer the freshly selected file (object URL); fall back to a saved path.
    const url = this.documentObjectUrl || this.data.documentPath;
    if (!url) {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  isFormValid(): boolean {
    // Check required fields
    if (!this.data.employeeName || this.data.employeeName.trim() === '') {
      return false;
    }
    if (!this.data.employeePhone) {
      return false;
    }
    if (!this.data.employeeEmail || this.data.employeeEmail.trim() === '') {
      return false;
    }
    if (!this.data.employeeStatus) {
      return false;
    }
    if ((this.isSysAdmin || this.data.employeeAction === 'add_employee') && 
        (!this.data.employeePassword || this.data.employeePassword.trim() === '')) {
      return false;
    }
    return true;
  }

  updateEmployee(employeeData: Employee): void {
    this.apiService.updateEmployee(employeeData).subscribe((data) => {
      if (data.isSuccess === true) {
        this._snackBar.open('Employee data updated successfully', 'Close', {
          duration: 3000,
        });
        this.dialogRef.close();
      } else {
        this._snackBar.open('Error occurred while updating details', 'Close', {
          duration: 3000,
        });
      }
    }, (error) => {
      this._snackBar.open('Error occurred while updating details', 'Close', {
        duration: 3000,
      });
    });
  }

  addEmployee(employeeData: Employee): void {
    if(employeeData.employeeAction =='add_employee'){
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      
      employeeData.employeeCreationDate = `${yyyy}-${mm}-${dd}`;
      
      this.apiService.addEmplyee(employeeData).subscribe((data) => {
        if (data != null) {
          this._snackBar.open('Employee data added successfully', 'Close', {
            duration: 3000,
          });
          this.dialogRef.close();
        } else {
          this._snackBar.open('Error occurred while adding details', 'Close', {
            duration: 3000,
          });
        }
      }, (error) => {
        this._snackBar.open('Error occurred while adding details', 'Close', {
          duration: 3000,
        });
      });
    } else {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');

      employeeData.employeeCreationDate = `${yyyy}-${mm}-${dd}`;

      this.apiService.addEmployee(employeeData).subscribe((data) => {
        if (data != null) {
          this._snackBar.open('Employee data added successfully', 'Close', {
            duration: 3000,
          });
          this.dialogRef.close();
        } else {
          this._snackBar.open('Error occurred while adding details', 'Close', {
            duration: 3000,
          });
        }
      }, (error) => {
        this._snackBar.open('Error occurred while adding details', 'Close', {
          duration: 3000,
        });
      });
    }
  }
}
